import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { BlocksSnippetAri } from '@atlassian/ari';

import { cfetch } from '@confluence/network';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import {
	type Snippet,
	SnippetBodyFormat,
	type SnippetOwner,
	SnippetProduct,
	type SnippetStatus,
} from '../snippetTypes.ts';
import { parseSnippetContent } from '../snippetUtils.ts';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
};

const BLOCK_SERVICE_API_URL = '/gateway/api/blocks/v1';
const SNIPPETS_PATH = '/snippets';

export class SnippetError extends Error {
	constructor(readonly status: number) {
		super(`Snippet error: ${status}`);
	}
}

export type SnippetData = {
	resourceId: string; // ARI of the Snippet
	snippetId: string;
	name: string; // User defined name
	description?: string; // User defined name
	body: string; // String of the type defined in `bodyFormat` (example: "ADF")
	bodyFormat: SnippetBodyFormat;
	product: SnippetProduct;
	label?: string;
	isFavorite: 0 | 1;
	status: SnippetStatus;
	createdBy: string;
	createdAt: string; // String of integer timestamp
	updatedAt: string; // String of integer timestamp
};

const transformSnippets = async (snippets: SnippetData[]): Promise<Snippet[]> => {
	const { displayName } = await getSessionData();
	return snippets.map((snippet: SnippetData) => transformSnippetData(snippet, displayName));
};

const transformSnippetData = (snippet: SnippetData, displayName: string | null): Snippet => {
	const createdBy: SnippetOwner = {
		accountId: snippet.createdBy,
		displayName:
			snippet.createdBy?.toLowerCase() === 'atlassian'
				? 'Atlassian'
				: displayName || snippet.createdBy,
	};

	return {
		ari: BlocksSnippetAri.parse(snippet.resourceId),
		snippetId: snippet.snippetId,
		name: snippet.name,
		description: snippet.description,
		bodyFormat: snippet.bodyFormat,
		body: parseSnippetContent(snippet.body),
		product: snippet.product,
		isFavorite: Boolean(snippet.isFavorite),
		status: snippet.status,
		createdBy,
		createdAt: new Date(Number(snippet.createdAt)),
		updatedAt: new Date(Number(snippet.updatedAt)),
	};
};

export type CreateSnippetBody = {
	name: string;
	body: ADFEntity;
	bodyFormat: SnippetBodyFormat;
	product: SnippetProduct;
};

type FetchSnippetScope = 'user' | 'global';

/**
 * Fetch snippets from the Block Service for Confluence. https://bitbucket.org/atlassian/block-service/src/main/
 * Please note that no error handling is done. Ensure you are handling bad response codes when using this.
 */
export const fetchSnippets = async (): Promise<Snippet[]> => {
	const [userSnippetsResult, globalSnippetsResult] = await Promise.allSettled([
		fetchSnippetData('user'),
		fetchSnippetData('global'),
	]);

	const successfulSnippets: SnippetData[] = [];

	if (userSnippetsResult.status === 'fulfilled') {
		successfulSnippets.push(...userSnippetsResult.value);
	} else {
		logResultError(userSnippetsResult.reason, 'user');
	}

	if (globalSnippetsResult.status === 'fulfilled') {
		successfulSnippets.push(...globalSnippetsResult.value);
	} else {
		logResultError(globalSnippetsResult.reason, 'global');
	}

	return transformSnippets(successfulSnippets);
};

const fetchSnippetData = async (scope: FetchSnippetScope): Promise<SnippetData[]> => {
	const params = new URLSearchParams({
		format: SnippetBodyFormat.ADF,
		product: SnippetProduct.CONFLUENCE,
		scope,
	});

	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}?${params.toString()}`, {
		method: 'GET',
		headers: COMMON_HEADERS,
	});

	if (!response.ok) {
		throw new SnippetError(response.status);
	}

	const responseData = await response.json();
	if (!responseData.success) {
		return [];
	}

	// Success and error can both be present, so we need to log the error if it is present
	if (Array.isArray(responseData.error) && responseData.error.length > 0) {
		responseData.error.forEach((error: any) => {
			const errorToLog = new Error(
				`Error when fetching ${scope} scoped snippets: ${error?.message || JSON.stringify(error)}`,
			);
			getMonitoringClient().submitError(errorToLog, {
				attribution: Attribution.INTEGRATIONS,
			});
		});
	}

	return responseData.success;
};

export const createSnippet = async (params: CreateSnippetBody): Promise<SnippetData> => {
	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}`, {
		method: 'POST',
		headers: COMMON_HEADERS,
		body: JSON.stringify(params),
	});

	if (!response.ok) {
		throw new SnippetError(response.status);
	}

	return response.json();
};

const logResultError = (reason: unknown, scope: FetchSnippetScope): void => {
	const reasonString = reason instanceof Error ? reason.message : JSON.stringify(reason);

	const errorToLog = new Error(`Error when fetching ${scope} scoped snippets: ${reasonString}`);
	getMonitoringClient().submitError(errorToLog, {
		attribution: Attribution.INTEGRATIONS,
	});
};
